import { useMemo, useState } from 'react'
import { Check, ChevronDown, Loader2, UserRound } from 'lucide-react'
import { useStaff } from '@/api/hooks'
import { StaffAvatar } from '@/components/staff-avatar'
import { Button } from '@/components/ui/button'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'

export function StaffFilter({
  value,
  disabled = false,
  onValueChange,
}: {
  value?: number
  disabled?: boolean
  onValueChange: (value: number | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const staff = useStaff()
  const selected = staff.data?.find((user) => user.id === value)
  const selectionLabel = selected?.name ?? (value ? 'selected staff' : 'all staff')
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return staff.data ?? []
    return (staff.data ?? []).filter(
      (user) =>
        user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
    )
  }, [search, staff.data])

  const choose = (staffId?: number) => {
    onValueChange(staffId)
    setOpen(false)
    setSearch('')
  }

  return (
    <PickerMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
    >
      <PickerMenuTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={`Project staff filter: ${selectionLabel}`}
          className="w-full justify-between font-normal md:w-[200px]"
        >
          <span className="flex min-w-0 items-center gap-2">
            <UserRound className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{selected?.name ?? (value ? 'Staff' : 'All staff')}</span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Filter by staff" align="end" className="w-[min(20rem,calc(100vw-2rem))]">
        <PickerMenuSearch
          value={search}
          onChange={setSearch}
          placeholder="Search staff"
          ariaLabel="Search project staff"
        />
        <PickerMenuList className="max-h-64">
          <PickerMenuItem onSelect={() => choose()} className="min-h-10 justify-between">
            All staff
            {value === undefined ? <Check className="size-4 text-primary" /> : null}
          </PickerMenuItem>
          <PickerMenuSeparator />
          {staff.isLoading ? (
            <div className="flex min-h-20 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading staff
            </div>
          ) : filtered.length ? (
            filtered.map((user) => (
              <PickerMenuItem
                key={user.id}
                onSelect={() => choose(user.id)}
                className="min-h-11 justify-between"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <StaffAvatar size="sm" name={user.name} photoUrl={user.photo_url} />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{user.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                  </span>
                </span>
                {user.id === value ? <Check className="size-4 shrink-0 text-primary" /> : null}
              </PickerMenuItem>
            ))
          ) : (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching staff</p>
          )}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}
